import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getNewsEvents } from "../../services/catalogueService";
import { loginVSUser } from "../../services/vsUserService";
import { 
  saveClientSession, 
  getClientSession, 
  clearClientSession, 
  isClientAuthenticated 
} from "../../services/clientAuth";
import "./Sidebar.css";
// bulletIcon removed if not used anywhere else, but let's keep it for now if News uses it via CSS
// Actually News uses title-bg3.png via CSS.


const legacyNewsItems = [
  { _id: "legacy-news-1", title: "Our services", slug: "legacy-services" },
  {
    _id: "legacy-news-2",
    title: "new design added in new creation",
    slug: "legacy-new-creation",
  },
  {
    _id: "legacy-news-3",
    title: "new catlouge added gents ring-1",
    slug: "legacy-gents-ring",
  },
  { _id: "legacy-news-4", title: "new catlouge added Vs-4", slug: "legacy-vs-4" },
  { _id: "legacy-news-5", title: "new catlouge added Vs-5", slug: "legacy-vs-5" },
  {
    _id: "legacy-news-6",
    title: "free registration and download",
    slug: "legacy-registration-download",
  },
];

export default function Sidebar({
  showNews = true,
  showNewCreation = true,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const isDownloadPage = location.pathname === "/download";
  const [news, setNews] = React.useState([]);
  const [newsLoading, setNewsLoading] = React.useState(true);
  const [loginEmail, setLoginEmail] = React.useState("");
  const [loginPassword, setLoginPassword] = React.useState("");
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);
  const [loginError, setLoginError] = React.useState("");
  const [isAuthenticated, setIsAuthenticated] = React.useState(isClientAuthenticated());
  const [userSession, setUserSession] = React.useState(getClientSession());

  // Force clear autofill - browsers sometimes ignore autoComplete="off"
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setLoginEmail("");
      setLoginPassword("");
    }, 100);
    return () => clearTimeout(timer);
  }, []);
  
  const newsMarqueeRef = React.useRef(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");

    if (!loginEmail || !loginPassword) {
      setLoginError("Please enter email and password.");
      return;
    }

    try {
      setIsLoggingIn(true);
      const response = await loginVSUser({ email: loginEmail, password: loginPassword });
      
      if (response?.isOk) {
        // Enforce admin approval check
        if (!response.data.isAuthorized) {
          setLoginError("Wait for admin approval");
          setIsLoggingIn(false);
          return;
        }

        saveClientSession(response.data);
        setIsAuthenticated(true);
        setUserSession(getClientSession());
        setLoginEmail("");
        setLoginPassword("");
        // If they are on register or login page, take them to download
        if (window.location.pathname === "/register" || window.location.pathname === "/login") {
          navigate("/download");
        } else {
          window.location.reload(); // Refresh to update protected states
        }
      }
    } catch (error) {
      setLoginError(error?.response?.data?.message || "Invalid credentials.");
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = () => {
    clearClientSession();
    setIsAuthenticated(false);
    setUserSession(null);
    navigate("/");
  };

  const handleForgotRedirect = () => {
    navigate("/login", { state: { focusForgot: true, forgotEmail: loginEmail } });
  };

  React.useEffect(() => {
    if (!showNews) return;
    const fetchNews = async () => {
      try {
        setNewsLoading(true);
        const data = await getNewsEvents();
        const activeNews = data
          .filter((item) => item?.isActive ?? item?.IsActive ?? true)
          .sort((a, b) => (a.sequence || a.Id || 0) - (b.sequence || b.Id || 0));
        setNews(activeNews.length ? activeNews : legacyNewsItems);
      } catch (error) {
        console.error("Failed to fetch sidebar news:", error);
        setNews(legacyNewsItems);
      } finally {
        setNewsLoading(false);
      }
    };
    fetchNews();
  }, [showNews]);



  return (
    <aside className="sidebar">
      {/* Member Login Section */}
      <div className="sidebar-section">
        <div className="section-title">
          <div className="title-white">Login</div>
        </div>
        <div className="sidebar-content login-box">
          {isAuthenticated ? (
            <div className="login-welcome">
              <p>Welcome <span className="user-name">{userSession?.user?.name || "Member"}</span>,</p>
              {isDownloadPage ? (
                <>
                  <p>You can download following</p>
                  <p>Catalogues by Click on it.</p>
                </>
              ) : (
                <>
                  <p>You can download Catalogues</p>
                  <p>from <Link to="/download" className="download-link">here</Link>.</p>
                </>
              )}
              <div className="login-actions">
                <button type="button" className="login-btn" onClick={handleLogout}>
                  Logout
                </button>
              </div>
            </div>
          ) : (
            <form className="login-form" onSubmit={handleLogin} autoComplete="off">
              {loginError && <p className="login-error-msg">{loginError}</p>}
              
              <div className="login-row">
                <label htmlFor="side-email">Email</label>
                <input
                  id="side-email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  className="login-input"
                  autoComplete="off"
                  required
                />
              </div>

              <div className="login-row">
                <label htmlFor="side-password">Password</label>
                <input
                  id="side-password"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  className="login-input"
                  autoComplete="new-password"
                  required
                />
              </div>

              <div className="login-actions">
                <button type="submit" className="login-btn" disabled={isLoggingIn}>
                  {isLoggingIn ? "..." : "Login"}
                </button>
                <button type="button" className="login-btn" onClick={handleForgotRedirect}>
                  Forgot Password
                </button>
              </div>

              <div className="login-footer">
                <Link to="/register">Register Here?</Link>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Spacer between Login and News */}
      <div className="sidebar-spacer" />

      {/* News and Events Section */}
      {showNews && (
        <div className="sidebar-section">
          <div className="section-title">
            <div className="title-white">News and Events</div>
          </div>
          <div className="sidebar-content">
            <div
              className="news-container"
              onMouseEnter={() => newsMarqueeRef.current?.stop()}
              onMouseLeave={() => newsMarqueeRef.current?.start()}
            >
              {newsLoading ? (
                <p className="news-status">Loading news...</p>
              ) : news.length > 0 ? (
                <marquee ref={newsMarqueeRef} direction="up" scrollamount="2">
                  {news.map((item) => (
                    <p key={item._id}>
                      <Link className="news-link" to={`/news/${item.slug || item._id}`}>
                        {item.title}
                      </Link>
                    </p>
                  ))}
                </marquee>
              ) : (
                <p className="news-status">No news available.</p>
              )}
            </div>
            <div className="news-more-container">
              <button
                type="button"
                className="more-btn"
                onClick={() => navigate("/news")}
              >
                More
              </button>
            </div>
          </div>
        </div>
      )}


    </aside>
  );
}
